import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { FiArrowRight, FiDownload, FiUsers, FiPackage, FiCopy, FiCheck, FiTerminal } from 'react-icons/fi';
import toast from 'react-hot-toast';
import api from '../utils/api';
import SkillCard, { type SkillData } from '../components/SkillCard';
import Leaderboard from '../components/Leaderboard';
import AgentBadge, { agentConfig } from '../components/AgentBadge';
import { seedSkills } from '../data/seedSkills';

/* =============================================
   ANIMATED SECTION WRAPPER
   ============================================= */
function ScrollSection({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
      className={className}
    >
      {children}
    </motion.section>
  );
}

/* =============================================
   ANIMATED COUNTER
   ============================================= */
function AnimatedCounter({ value, suffix = '' }: { value: number; suffix?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    const duration = 2000;
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(value * eased));
      if (progress >= 1) clearInterval(interval);
    }, 16);
    return () => clearInterval(interval);
  }, [isInView, value]);

  return (
    <span ref={ref} className="tabular-nums">
      {display.toLocaleString()}{suffix}
    </span>
  );
}

/* =============================================
   COPY BUTTON
   ============================================= */
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard!');
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="p-1.5 rounded-md text-slate-500 hover:text-brand-400 hover:bg-white/10 transition-all"
      title="Copy to clipboard"
    >
      {copied ? <FiCheck size={14} className="text-green-400" /> : <FiCopy size={14} />}
    </button>
  );
}

/* =============================================
   HOME PAGE
   ============================================= */
export default function Home() {
  const [featuredSkills, setFeaturedSkills] = useState<SkillData[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<{ skills: number; installs: number; contributors: number } | null>(null);

  useEffect(() => {
    async function fetchSkills() {
      try {
        const response = await api.get('/skills', {
          params: { sort: 'popular', per_page: 6 },
        });
        const fetched = response.data.skills || [];
        setFeaturedSkills(fetched.length > 0 ? fetched : seedSkills.slice(0, 6));
      } catch {
        setFeaturedSkills(seedSkills.slice(0, 6));
      } finally {
        setLoading(false);
      }
    }

    async function fetchStats() {
      try {
        const response = await api.get('/stats');
        setStats(response.data);
      } catch {
        // Stats will just not show if API fails
      }
    }

    fetchSkills();
    fetchStats();
  }, []);

  const installCmd = 'npx skillsai add strongly-ai/code-review';

  const allAgents = Object.keys(agentConfig);

  const howItWorks = [
    {
      step: '01',
      title: 'Discover',
      description: 'Browse the open directory of skills built by the community. Filter by agent, category, or search.',
      icon: <FiPackage size={28} />,
      gradient: 'from-brand-500 to-brand-400',
    },
    {
      step: '02',
      title: 'Install',
      description: 'One command to install any skill into your AI agent. Works with 40+ coding agents out of the box.',
      icon: <FiTerminal size={28} />,
      gradient: 'from-brand-purple-500 to-brand-purple-400',
    },
    {
      step: '03',
      title: 'Create',
      description: 'Write a SKILL.md, push to GitHub, and publish to the directory. Share your expertise with the world.',
      icon: <FiArrowRight size={28} />,
      gradient: 'from-emerald-500 to-emerald-400',
    },
  ];

  return (
    <div className="pt-16">
      {/* ============ HERO ============ */}
      <section className="relative min-h-[90vh] flex items-center justify-center grid-bg overflow-hidden">
        {/* Ambient glow orbs */}
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-brand-500/[0.07] blur-[150px] pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-brand-purple-600/[0.05] blur-[130px] pointer-events-none" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-xs text-slate-400 mb-8"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              Open Source Skills Directory
            </motion.div>

            {/* Headline */}
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black leading-[0.95] tracking-tight mb-6">
              <span className="text-white">The Open Skills</span>
              <br />
              <span className="text-white">Directory for </span>
              <span className="gradient-text">AI Agents</span>
            </h1>

            {/* Subheading */}
            <p className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              Discover, install, and share reusable skills for Claude Code, Cursor,
              Windsurf, Codex, and 40+ AI coding agents.
            </p>

            {/* CTA Row */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <Link to="/browse">
                <motion.button
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.98 }}
                  className="btn-gradient text-base px-8 py-4 rounded-xl flex items-center gap-2"
                >
                  <span>Browse Skills</span>
                  <FiArrowRight size={18} />
                </motion.button>
              </Link>

              {/* Terminal Install Block */}
              <div className="terminal-block">
                <div className="terminal-header">
                  <div className="terminal-dot bg-red-500/80" />
                  <div className="terminal-dot bg-yellow-500/80" />
                  <div className="terminal-dot bg-green-500/80" />
                  <span className="text-[10px] text-slate-500 ml-2">Terminal</span>
                </div>
                <div className="terminal-body flex items-center gap-3">
                  <span className="text-slate-600 select-none">$</span>
                  <code className="text-brand-300 text-sm">{installCmd}</code>
                  <CopyButton text={installCmd} />
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom gradient fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-surface to-transparent" />
      </section>

      {/* ============ STATS BAR ============ */}
      {stats && (stats.skills > 0 || stats.installs > 0 || stats.contributors > 0) && (
        <ScrollSection className="py-12 border-y border-white/5">
          <div className="max-w-5xl mx-auto px-4">
            <div className="grid grid-cols-3 gap-8">
              {[
                { label: 'Skills Published', value: stats.skills, icon: <FiPackage className="text-brand-400" /> },
                { label: 'Total Installs', value: stats.installs, icon: <FiDownload className="text-brand-purple-400" /> },
                { label: 'Contributors', value: stats.contributors, icon: <FiUsers className="text-emerald-400" /> },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    {stat.icon}
                    <span className="text-3xl sm:text-4xl font-bold text-white">
                      <AnimatedCounter value={stat.value} />
                    </span>
                  </div>
                  <span className="text-sm text-slate-500">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </ScrollSection>
      )}

      {/* ============ FEATURED SKILLS ============ */}
      <ScrollSection className="py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Featured <span className="gradient-text">Skills</span>
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              The most popular skills, curated by the community.
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="glass-card p-6 h-52 shimmer rounded-2xl" />
              ))}
            </div>
          ) : featuredSkills.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredSkills.map((skill, idx) => (
                <SkillCard key={skill._id} skill={skill} index={idx} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-slate-500 mb-6">No skills published yet. Be the first!</p>
              <Link to="/submit">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  className="btn-gradient px-6 py-3 text-sm rounded-xl inline-flex items-center gap-2"
                >
                  <span>Submit a Skill</span>
                  <FiArrowRight size={16} />
                </motion.button>
              </Link>
            </div>
          )}

          {featuredSkills.length > 0 && (
            <div className="text-center mt-10">
              <Link to="/browse">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  className="btn-ghost px-8 py-3 text-sm inline-flex items-center gap-2"
                >
                  View All Skills
                  <FiArrowRight size={16} />
                </motion.button>
              </Link>
            </div>
          )}
        </div>
      </ScrollSection>

      {/* ============ LEADERBOARD ============ */}
      {featuredSkills.length > 0 && (
        <ScrollSection className="py-24 bg-surface-100">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  Top <span className="gradient-text">Skills</span>
                </h2>
                <p className="text-slate-400 mb-8">
                  See which skills are rising to the top. Updated in real-time based on installs and community engagement.
                </p>
                <Link to="/browse?sort=popular">
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    className="btn-ghost px-6 py-2.5 text-sm inline-flex items-center gap-2"
                  >
                    Full Leaderboard
                    <FiArrowRight size={14} />
                  </motion.button>
                </Link>
              </div>
              <Leaderboard skills={featuredSkills} title="Top Skills" />
            </div>
          </div>
        </ScrollSection>
      )}

      {/* ============ HOW IT WORKS ============ */}
      <ScrollSection className="py-24">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              How It <span className="gradient-text">Works</span>
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              Three simple steps from discovery to deployment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howItWorks.map((item, idx) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15, duration: 0.5 }}
                className="glass-card p-8 text-center group"
              >
                {/* Step Number */}
                <div className="text-[10px] font-mono text-slate-500 mb-4 tracking-widest">
                  STEP {item.step}
                </div>

                {/* Icon */}
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${item.gradient} mx-auto mb-6 flex items-center justify-center text-white
                  shadow-lg group-hover:scale-110 transition-all duration-300`}>
                  {item.icon}
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>

                {/* Description */}
                <p className="text-sm text-slate-400 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </ScrollSection>

      {/* ============ EVALUATE YOUR SKILL ============ */}
      <ScrollSection className="py-24 bg-surface-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-500/10 border border-brand-500/20 text-xs font-medium text-brand-300 mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-400 animate-pulse" />
                Free tool · No login
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Score your skill in <span className="gradient-text">seconds</span>
              </h2>
              <p className="text-slate-400 mb-6 leading-relaxed">
                Paste your SKILL.md or drop in a GitHub URL to get an instant 0 to 100
                score with prioritized recommendations, grounded in documented best
                practices. Runs entirely in your browser; nothing is uploaded.
              </p>
              <ul className="space-y-2 mb-8 text-sm text-slate-400">
                <li className="flex items-start gap-2">
                  <FiCheck className="text-emerald-400 mt-0.5 shrink-0" />
                  <span>29 mechanical checks across structure, naming, description, body, and safety</span>
                </li>
                <li className="flex items-start gap-2">
                  <FiCheck className="text-emerald-400 mt-0.5 shrink-0" />
                  <span>Top 5 actionable fixes ranked by impact</span>
                </li>
                <li className="flex items-start gap-2">
                  <FiCheck className="text-emerald-400 mt-0.5 shrink-0" />
                  <span>No login required, no data retained after scoring</span>
                </li>
              </ul>
              <Link to="/evaluate">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  className="btn-gradient px-6 py-3 rounded-xl text-sm inline-flex items-center gap-2"
                >
                  <span className="inline-flex items-center gap-2">
                    Evaluate your Skill
                    <FiArrowRight />
                  </span>
                </motion.button>
              </Link>
            </div>

            {/* Faux scorecard */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="glass-card p-8"
            >
              <div className="flex items-center gap-6 mb-6">
                <div className="relative w-28 h-28 shrink-0">
                  <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="42" stroke="rgba(255,255,255,0.08)" strokeWidth="7" fill="transparent" />
                    <circle
                      cx="50" cy="50" r="42"
                      stroke="#34d399"
                      strokeWidth="7"
                      strokeLinecap="round"
                      fill="transparent"
                      strokeDasharray={2 * Math.PI * 42}
                      strokeDashoffset={2 * Math.PI * 42 * (1 - 0.92)}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="text-3xl font-bold tabular-nums">92</div>
                    <div className="text-[10px] font-semibold text-emerald-300">Excellent</div>
                  </div>
                </div>
                <div>
                  <div className="text-xs font-mono text-slate-500 mb-1">notion-project-setup</div>
                  <div className="text-sm font-semibold text-white">Sample scorecard</div>
                </div>
              </div>

              <div className="space-y-3">
                {[
                  { label: 'Structure', pct: 100 },
                  { label: 'Description', pct: 90 },
                  { label: 'Body quality', pct: 85 },
                  { label: 'Progressive disclosure', pct: 95 },
                ].map((r) => (
                  <div key={r.label}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-400">{r.label}</span>
                      <span className="text-slate-500 tabular-nums">{r.pct}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${r.pct >= 90 ? 'bg-emerald-400' : 'bg-brand-500'}`}
                        style={{ width: `${r.pct}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </ScrollSection>

      {/* ============ COMPATIBLE AGENTS ============ */}
      <ScrollSection className="py-24 bg-surface-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Works With <span className="gradient-text">Every Agent</span>
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              Skills are universal. Install once, use with any AI coding agent.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {allAgents.map((agent, idx) => (
              <motion.div
                key={agent}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.03, duration: 0.3 }}
              >
                <AgentBadge agentName={agent} size="md" showLabel={true} />
              </motion.div>
            ))}
          </div>
        </div>
      </ScrollSection>

      {/* ============ CTA ============ */}
      <ScrollSection className="py-32 relative overflow-hidden">
        {/* Background glow */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-[600px] h-[600px] rounded-full bg-gradient-to-r from-brand-500/[0.06] to-brand-purple-600/[0.04] blur-[120px]" />
        </div>

        <div className="relative z-10 max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Publish Your First <span className="gradient-text">Skill</span>
          </h2>
          <p className="text-lg text-slate-400 mb-10 leading-relaxed">
            Write a SKILL.md, push to GitHub, and share your expertise with
            AI agent users worldwide.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/submit">
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.98 }}
                className="btn-gradient px-8 py-4 text-base rounded-xl flex items-center gap-2"
              >
                <span>Submit a Skill</span>
                <FiArrowRight size={18} />
              </motion.button>
            </Link>
            <Link to="/docs">
              <motion.button
                whileHover={{ scale: 1.03 }}
                className="btn-ghost px-8 py-4 text-base rounded-xl"
              >
                Read the Docs
              </motion.button>
            </Link>
          </div>
        </div>
      </ScrollSection>
    </div>
  );
}
